import type { CircuitSuffixOf, ResolveCircuitEditsArgs } from "./types";

const SEPARATOR = "·";

/**
 * The part of a circuit id that is NOT the board — "A38", "grp·Motor", "Ring".
 *
 * Found by matching a KNOWN board name as a prefix, longest first, never by
 * splitting on the separator: a board carries one itself ("PAS/HES · Erdung"),
 * and a run's own grouping suffix carries one too ("grp·Motor").
 *
 * The fallback to the first separator is for a STORED id whose board no longer
 * exists on this plan — which is the whole case this module is for. A wrong
 * split there can only produce a suffix that matches nothing live, so it
 * degrades to "no match" and never to the wrong match.
 */
export const circuitSuffixOf: CircuitSuffixOf = ({ circuitId, boardNames }) => {
  const byLongest = [...boardNames].sort((a, b) => b.length - a.length);
  for (const name of byLongest) {
    const prefix = `${name}${SEPARATOR}`;
    if (circuitId.startsWith(prefix)) return circuitId.slice(prefix.length);
  }
  const at = circuitId.indexOf(SEPARATOR);
  return at >= 0 ? circuitId.slice(at + 1) : null;
};

/**
 * Re-address circuit-keyed edits onto the circuits that are actually here.
 *
 * `circuitId` is `${board}·${circ}` and the board half is decided per run — the
 * agent's own audit: project 151 put 78 of 85 BoQ lines on the wrong board
 * between two runs of the same plan. So an edit stored under "GHV·A38" has to
 * still be his edit when the run comes back calling it "DV·A38".
 *
 * The rule, and it REFUSES rather than guesses — the precedence at the top of
 * KARL.md, applied to a key:
 *
 * 1. An exact id wins outright.
 * 2. Otherwise the suffix must name exactly ONE unclaimed circuit here. Two
 *    boards carrying "A38" is not a thing we may pick between.
 * 3. And exactly one stored entry may claim it. Two edits landing on one
 *    circuit is a document we cannot read, so neither is applied.
 */
// Typed on its own args rather than through the exported alias: a generic
// signature cannot bind destructured parameters through a type annotation.
export const resolveCircuitEdits = <T>({
  edits,
  liveCircuitIds,
  boardNames,
}: ResolveCircuitEditsArgs<T>): Record<string, T> => {
  const resolved: Record<string, T> = {};
  if (!edits) return resolved;

  const live = new Set(liveCircuitIds);
  const orphans: string[] = [];
  for (const [circuitId, value] of Object.entries(edits)) {
    if (live.has(circuitId)) resolved[circuitId] = value;
    else orphans.push(circuitId);
  }
  if (!orphans.length) return resolved;

  const bySuffix = new Map<string, string[]>();
  for (const circuitId of liveCircuitIds) {
    const suffix = circuitSuffixOf({ circuitId, boardNames });
    if (!suffix) continue;
    const found = bySuffix.get(suffix);
    if (found) found.push(circuitId);
    else bySuffix.set(suffix, [circuitId]);
  }

  // Collected before anything is written, so "two entries claim one circuit"
  // can be refused rather than settled by iteration order.
  const claims = new Map<string, string[]>();
  for (const circuitId of orphans) {
    const suffix = circuitSuffixOf({ circuitId, boardNames });
    if (!suffix) continue;
    const candidates = (bySuffix.get(suffix) ?? []).filter(
      (candidate) => !(candidate in resolved),
    );
    if (candidates.length !== 1) continue;
    const target = candidates[0];
    const claimed = claims.get(target);
    if (claimed) claimed.push(circuitId);
    else claims.set(target, [circuitId]);
  }

  claims.forEach((claimants, target) => {
    if (claimants.length !== 1) return;
    resolved[target] = edits[claimants[0]];
  });
  return resolved;
};
